import {LoadingButton} from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import {useSnackbar} from 'notistack';
import React, {ReactNode, useState} from 'react';

import API from '../../../api/axios';
import {apiBaseUrl} from '../../../api/urls';
import {BackhaulerBulkActionResponse} from '../../../interfaces/Backhauler';
import {CloseSnackbarAction} from '../../common/CloseSnackbarButton';

interface BackHaulersBulkActionButtonProps {
  action: 'start' | 'stop';
  confirm?: boolean;
  component?: React.FC;
  componentProps?: any;
  onSubmitted?: Function;
  children?: ReactNode;
}

export const BackHaulersBulkActionButton: React.FC<
  BackHaulersBulkActionButtonProps
> = ({
  action,
  confirm = false,
  component = Button,
  componentProps = {},
  onSubmitted,
  children,
}) => {
  const {enqueueSnackbar} = useSnackbar();
  const Component = component;
  const actionLabel = action === 'start' ? 'Start' : 'Stop';

  const [isOpened, setIsOpened] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = () => setIsOpened(true);
  const closeModal = () => setIsOpened(false);

  const submit = async () => {
    setIsSubmitting(true);

    try {
      const resp = await API.patch<BackhaulerBulkActionResponse>(
        `${apiBaseUrl}/backhauler/${action}-all`
      );
      const failed = resp.data.failed ?? [];

      enqueueSnackbar(
        failed.length
          ? `${resp.data.message}: ${failed.map((f) => f.name).join(', ')}`
          : resp.data.message,
        {
          variant: failed.length ? 'warning' : 'success',
          action: CloseSnackbarAction,
        }
      );
      onSubmitted?.();
      closeModal();
    } catch (error: any) {
      const message = error?.response?.data?.message ?? 'There is an error';
      enqueueSnackbar(message, {
        variant: 'error',
        action: CloseSnackbarAction,
      });
    }

    setIsSubmitting(false);
  };

  return (
    <>
      <Component
        {...componentProps}
        disabled={isSubmitting || componentProps.disabled}
        onClick={confirm ? openModal : submit}
      >
        {children}
      </Component>

      {confirm ? (
        <Dialog open={isOpened} onClose={closeModal}>
          <DialogTitle>{actionLabel} all backhaulers</DialogTitle>

          <DialogContent>
            <DialogContentText>
              {action === 'stop'
                ? 'Are you sure you want to stop every backhauler of this company? Data collection will stop until they are started again.'
                : 'Are you sure you want to start every backhauler of this company?'}
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button onClick={closeModal}>Cancel</Button>

            <LoadingButton
              variant="contained"
              loading={isSubmitting}
              autoFocus
              onClick={() => submit()}
              color={action === 'stop' ? 'error' : 'primary'}
            >
              {actionLabel} all
            </LoadingButton>
          </DialogActions>
        </Dialog>
      ) : null}
    </>
  );
};
